<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ChevronRight, Bell, Moon, Sun, Eye, X, HelpCircle } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAppStore } from '@/stores/app.store';
import { useUserStore } from '@/stores/user.store';
import { ROLE_ADMIN, ROLE_ORDER, roleRank } from '@/util/roles';
import { storeToRefs } from 'pinia';
import { useDepotTour } from '@/composables/useDepotTour';

const route = useRoute();
const router = useRouter();

const { runNow, hasTour } = useDepotTour();

const currentRouteName = computed(() => {
	return route.name as string | undefined;
});
const appStore = useAppStore();
const userStore = useUserStore();
const { theme } = storeToRefs(appStore);

interface Crumb {
	label: string;
	path?: string;
}

// Derived generically from the matched route records, so a page's name is
// declared once — on its route — and the router, the navigation and this trail
// cannot drift apart. Replaces a hand-maintained path map that had already
// fallen behind the routes, silently, because nothing fails when it is stale.
const crumbs = computed<Crumb[]>(() => {
	const matched = route.matched
		.map((record) => {
			const label =
				(record.meta?.breadcrumb as string) || (record.meta?.title as string) || '';
			return {
				label,
				// Parameterised paths can't be navigated to directly.
				path: record.path.includes(':') ? undefined : record.path,
			};
		})
		.filter((crumb) => {
			return !!crumb.label;
		});

	return matched.length ? matched : [{ label: '125 Insights' }];
});

const handleCrumb = (path?: string) => {
	if (path) {
		router.push(path);
	}
};

const toggleTheme = () => {
	return appStore.toggleTheme();
};

// Role preview: act as a lower role to check what that role's UI looks like.
// Gated on realRank, never on rank — gate it on the effective rank and the
// control vanishes the moment it is used, taking the way back with it.
const canPreviewRoles = computed(() => {
	return userStore.realRank >= roleRank(ROLE_ADMIN);
});

const actableRoles = computed(() => {
	return ROLE_ORDER.filter((role) => {
		return roleRank(role) < userStore.realRank;
	});
});

const actAs = (role: string) => {
	userStore.setRoleOverride(role);

	// Lowering your rank where you stand would let the guard bounce you to
	// Forbidden, which renders outside the depot shell and so without these
	// controls. The dashboard is where that role's session would begin anyway.
	router.push('/depot');
};

// No navigation on reset: it only ever raises privilege, so no guard objects.
const resetRole = () => {
	userStore.clearRoleOverride();
};
</script>

<template>
	<header
		class="ds-topbar sticky top-0 z-30 flex h-14 items-center gap-3.5 border-b border-[color:var(--depot-border)] px-8 max-md:px-4"
	>
		<nav
			class="flex items-center gap-2 text-[13px] text-[color:var(--fg-faint)]"
			aria-label="Breadcrumb"
			data-tour="shell-breadcrumb"
		>
			<template v-for="(c, i) in crumbs" :key="i">
				<ChevronRight v-if="i > 0" :size="12" :stroke-width="1.4" />
				<span
					:class="
						i === crumbs.length - 1
							? 'text-[color:var(--fg)]'
							: c.path
								? 'cursor-pointer hover:text-[color:var(--fg)]'
								: ''
					"
					@click="i === crumbs.length - 1 ? null : handleCrumb(c.path)"
					>{{ c.label }}</span
				>
			</template>
		</nav>
		<div class="flex-1" />
		<div
			v-if="canPreviewRoles"
			class="mr-2 flex items-center gap-1"
			data-tour="shell-role-preview"
		>
			<DropdownMenu>
				<DropdownMenuTrigger as-child>
					<Button
						:variant="userStore.isPreviewingRole ? 'accent' : 'ghost'"
						size="sm"
						type="button"
						:title="
							userStore.isPreviewingRole
								? 'You are viewing the site as a lower role'
								: 'View the site as a lower role'
						"
					>
						<Eye :size="14" :stroke-width="1.6" />
						{{
							userStore.isPreviewingRole
								? `Acting as ${userStore.roleOverride}`
								: 'Act as'
						}}
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end">
					<DropdownMenuLabel>View the site as</DropdownMenuLabel>
					<DropdownMenuSeparator />
					<DropdownMenuItem v-for="role in actableRoles" :key="role" @click="actAs(role)">
						{{ role }}
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
			<Button
				v-if="userStore.isPreviewingRole"
				variant="ghost"
				size="sm"
				type="button"
				title="Return to your own role"
				@click="resetRole"
			>
				<X :size="14" :stroke-width="1.6" />
				Reset
			</Button>
		</div>
		<div class="flex items-center gap-1">
			<!-- Hidden rather than disabled on a page with no tour, so it never looks like a
			     control that does nothing. It ignores seen state, muting and the session cap:
			     it is the way back from all three. -->
			<Button
				v-if="hasTour(currentRouteName)"
				variant="ghost"
				size="icon-sm"
				type="button"
				title="Take the tour for this page"
				data-tour="shell-tour-button"
				@click="runNow(currentRouteName)"
			>
				<HelpCircle :size="14" :stroke-width="1.6" />
			</Button>
			<!-- <Button
				variant="ghost"
				size="icon-sm"
				class="relative"
				type="button"
				title="Notifications"
			>
				<Bell :size="14" :stroke-width="1.6" />
				<span
					class="absolute right-[6px] top-[6px] size-1.5 rounded-full bg-[color:var(--depot-accent)] shadow-[0_0_0_2px_var(--bg)]"
				/>
			</Button> -->
			<Button
				variant="ghost"
				size="icon-sm"
				type="button"
				title="Toggle theme"
				@click="toggleTheme"
			>
				<Sun v-if="theme === 'dark'" :size="14" :stroke-width="1.6" />
				<Moon v-else :size="14" :stroke-width="1.6" />
			</Button>
		</div>
	</header>
</template>
